import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  ManagerProgressionTaskState,
  type Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { SportBall } from '@sorare/core/src/atoms/icons/SportBall.tsx';
import { Trophy } from '@sorare/core/src/atoms/icons/Trophy';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isTypes } from '@sorare/core/src/lib/gql';

import { MissionSection } from '#src/components/missions/MissionSection/index.tsx';
import { ProgressTaskCard } from '#src/components/missions/ProgressTaskCard/index.tsx';
import { SetCollectionTaskDialog } from '#src/components/missions/SetCollectionTask/SetCollectionTaskDialog/index.tsx';
import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';

import type { MyCollectionTasksPage_taskInterface } from './__generated__/index.graphql.ts';

const ICON_COLOR = 'var(--c-inventory-yellow)';

type Props = {
  tasks: MyCollectionTasksPage_taskInterface[];
  rarity: Rarity;
  onRefetch: () => void;
};

const hasCompetition = (task: MyCollectionTasksPage_taskInterface) =>
  isTypes(task, ['CardsCountTask', 'CompleteCollectionsCountTask']) &&
  task.competitions.some(({ slug }) => !!slug);

export const MyCollectionTasksPage = ({ tasks, rarity, onRefetch }: Props) => {
  const { sport } = useSportContext();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const {
    onClaim,
    loadingTaskId,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  const claimNowTasks = tasks.filter(
    task => task.aasmState === ManagerProgressionTaskState.COMPLETED
  );
  const inProgressLeagueTasks = tasks.filter(
    task =>
      (task.aasmState === ManagerProgressionTaskState.READY ||
        task.aasmState === ManagerProgressionTaskState.ASSIGNED) &&
      hasCompetition(task)
  );
  const inProgressGeneralTasks = tasks.filter(
    task =>
      (task.aasmState === ManagerProgressionTaskState.READY ||
        task.aasmState === ManagerProgressionTaskState.ASSIGNED) &&
      !hasCompetition(task)
  );
  const completedTasks = tasks.filter(
    task => task.aasmState === ManagerProgressionTaskState.CLAIMED
  );

  const renderTasks = (taskList: typeof tasks) =>
    taskList.map(task => {
      if (!isTypes(task, ['CardsCountTask', 'CompleteCollectionsCountTask'])) {
        return null;
      }
      const { progress, target, competitions } = task;
      const category = competitions
        .map(({ displayName }) => displayName)
        .filter(Boolean)
        .join(', ');
      const isCardsCount = isTypes(task, ['CardsCountTask']);

      return (
        <ProgressTaskCard
          key={task.id}
          task={task}
          category={category}
          description={
            isCardsCount ? (
              <FormattedMessage
                defaultMessage="Collect {target} cards"
                values={{ target }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Complete {target} collections"
                values={{ target }}
              />
            )
          }
          accentColor="var(--c-inventory-yellow)"
          titleColor="var(--c-set-surface-primary)"
          progress={progress}
          target={target}
          progressTrailing={
            isCardsCount ? (
              <ScarcityIcon rarity={rarity} />
            ) : (
              <Collection size={2} />
            )
          }
          onClaim={async () => onClaim(task)}
          loading={loadingTaskId === task.id}
          onClick={() => setSelectedTaskId(task.id)}
          selected={selectedTaskId === task.id}
        />
      );
    });

  return (
    <>
      {claimNowTasks.length > 0 && (
        <MissionSection
          icon={<FontAwesomeIcon icon={iconGift} />}
          iconColor={ICON_COLOR}
          title={<FormattedMessage defaultMessage="Claim Now" />}
        >
          {renderTasks(claimNowTasks)}
        </MissionSection>
      )}
      {inProgressLeagueTasks.length > 0 && (
        <MissionSection
          icon={<Trophy size={2} />}
          iconColor={ICON_COLOR}
          title={<FormattedMessage defaultMessage="League" />}
        >
          {renderTasks(inProgressLeagueTasks)}
        </MissionSection>
      )}
      {inProgressGeneralTasks.length > 0 && (
        <MissionSection
          icon={<SportBall sport={sport} size={2} />}
          iconColor={ICON_COLOR}
          title={<FormattedMessage defaultMessage="General" />}
        >
          {renderTasks(inProgressGeneralTasks)}
        </MissionSection>
      )}
      {completedTasks.length > 0 && (
        <MissionSection
          icon={<FontAwesomeIcon icon={iconCheck} />}
          iconColor={ICON_COLOR}
          title={<FormattedMessage defaultMessage="Completed" />}
        >
          {renderTasks(completedTasks)}
        </MissionSection>
      )}
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
          onRefetch();
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
          onRefetch();
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
          onRefetch();
        }}
      />
      <SetCollectionTaskDialog
        open={!!selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
        taskId={selectedTaskId ?? ''}
      />
    </>
  );
};

MyCollectionTasksPage.fragments = {
  taskInterface: gql`
    fragment MyCollectionTasksPage_taskInterface on TaskInterface {
      id
      aasmState
      ... on CardsCountTask {
        id
        progress
        target
        competitions {
          slug
          displayName
        }
      }
      ... on CompleteCollectionsCountTask {
        id
        progress
        target
        competitions {
          slug
          displayName
        }
      }
      ...TaskClaimedDialogs_taskInterface
      ...ProgressTaskCard_taskInterface
    }
    ${TaskClaimedDialogs.fragments.taskInterface}
    ${ProgressTaskCard.fragments.taskInterface}
  ` as TypedDocumentNode<MyCollectionTasksPage_taskInterface>,
};
